import { supabase } from './supabase.ts';
import { TenantDocument, DocumentType } from '../types/document.ts';

export const documentService = {
  async uploadDocument(file: File, tenantId: string, documentType: DocumentType) {
    const fileExt = file.name.split('.').pop();
    const fileName = `tenant-documents/${tenantId}/${documentType}_${Date.now()}.${fileExt}`;
    
    // Upload file to Supabase storage
    const { error: uploadError } = await supabase.storage
      .from('documents')  // Using 'documents' as the bucket name
      .upload(fileName, file);

    if (uploadError) {
      throw new Error(`Error uploading document: ${uploadError.message}`);
    }

    // Create document record in database
    const { data: docData, error: docError } = await supabase
      .from('tenant_documents')
      .insert([{
        tenant_id: tenantId,
        document_type: documentType,
        file_name: file.name,
        file_path: fileName  // Store the complete path
      }])
      .select()
      .single();

    if (docError) {
      throw new Error(`Error creating document record: ${docError.message}`);
    }

    return docData;
  },

  async getDocuments(tenantId: string) {
    const { data, error } = await supabase
      .from('tenant_documents')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Error fetching documents: ${error.message}`);
    }

    return data as TenantDocument[];
  },

  async deleteDocument(documentId: string) {
    // First get the document to get the file path
    const { data: doc, error: fetchError } = await supabase
      .from('tenant_documents')
      .select('file_path')
      .eq('id', documentId)
      .single();

    if (fetchError) {
      throw new Error(`Error fetching document: ${fetchError.message}`);
    }

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('documents')
      .remove([doc.file_path]);

    if (storageError) {
      throw new Error(`Error deleting file: ${storageError.message}`);
    }

    // Delete from database
    const { error: deleteError } = await supabase
      .from('tenant_documents')
      .delete()
      .eq('id', documentId);

    if (deleteError) {
      throw new Error(`Error deleting document record: ${deleteError.message}`);
    }
  },

  async getDocumentUrl(filePath: string) {
    // Generate a long-lived signed URL (max allowed is 7 days)
    const { data, error } = await supabase.storage
      .from('documents')
      .createSignedUrl(filePath, 60 * 60 * 24 * 7); // 7 days
    
    if (error) {
      throw new Error(`Error getting signed URL: ${error.message}`);
    }

    // Store the expiry timestamp in localStorage
    const expiryTime = Date.now() + (60 * 60 * 24 * 6 * 1000); // 6 days in ms
    localStorage.setItem(`doc_expiry_${filePath}`, expiryTime.toString());
    
    return data?.signedUrl;
  },

  async getOrRefreshUrl(filePath: string) {
    const expiryTime = localStorage.getItem(`doc_expiry_${filePath}`);
    const now = Date.now();

    // If URL is close to expiry or doesn't exist, generate new one
    if (!expiryTime || parseInt(expiryTime) - now < 24 * 60 * 60 * 1000) { // 1 day buffer
      return this.getDocumentUrl(filePath);
    }

    // Return existing URL from cache if available
    const cachedUrl = localStorage.getItem(`doc_url_${filePath}`);
    if (cachedUrl) {
      return cachedUrl;
    }

    // Generate new URL if no cached version exists
    return this.getDocumentUrl(filePath);
  }
};
